﻿import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ChartBarIcon,
  UserGroupIcon,
  BuildingLibraryIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const INDICADORES_DEFAULT = {
  faturamento: 0,
  despesas: 0,
  lucro: 0,
  margemBruta: 0,
  saldoContas: 0,
  totalLancamentos: 0,
  totalContasAtivas: 0,
  receitaOperacional: 0,
  receitaNaoOperacional: 0,
  despesasOperacionais: 0,
  despesasTributarias: 0,
  despesasFinanceiras: 0
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const getTipoLancamentoMeta = (tipo) => {
  if (tipo === 'receita') {
    return {
      label: 'Receita',
      signal: '+',
      valueClass: 'text-success-600',
      badgeClass: 'badge-success'
    };
  }

  if (tipo === 'transferencia') {
    return {
      label: 'Transferencia',
      signal: '↔',
      valueClass: 'text-blue-600',
      badgeClass: 'badge-info'
    };
  }

  return {
    label: 'Despesa',
    signal: '-',
    valueClass: 'text-danger-600',
    badgeClass: 'badge-danger'
  };
};

const Dashboard = () => {
  const [indicadores, setIndicadores] = useState(INDICADORES_DEFAULT);

  const [graficos, setGraficos] = useState({
    mensal: [],
    categorias: []
  });

  const [ultimosLancamentos, setUltimosLancamentos] = useState([]);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date().setMonth(new Date().getMonth() - 6), 'yyyy-MM-dd'),
    data_fim: format(new Date(), 'yyyy-MM-dd'),
    regime: 'caixa',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas'
  });

  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  useEffect(() => {
    carregarDropdowns();
  }, []);

  const carregarDropdowns = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias')
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros do dashboard:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      // Carregar dashboard
      const response = await axios.get('/api/dashboard', {
        params: filtros
      });
      
      setIndicadores({
        ...INDICADORES_DEFAULT,
        ...Object.fromEntries(
          Object.entries(response.data?.indicadores || {}).map(([k, v]) => [k, toNumber(v)])
        )
      });
      setGraficos(response.data?.graficos || { mensal: [], categorias: [] });
      
      // Carregar últimos lançamentos
      const lancamentosResponse = await axios.get('/api/lancamentos', {
        params: {
          ...filtros,
          limit: 5
        }
      });
      
      setUltimosLancamentos(
        Array.isArray(lancamentosResponse.data)
          ? lancamentosResponse.data.map((l) => ({ ...l, valor: toNumber(l.valor) }))
          : []
      );
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setCarregando(false);
    }
  };

  const cards = [
    {
      title: 'Faturamento',
      value: `R$ ${indicadores.faturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: CurrencyDollarIcon,
      color: 'success',
      change: '+12.5%',
      description: 'Total de receitas do período'
    },
    {
      title: 'Despesas',
      value: `R$ ${indicadores.despesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: ArrowTrendingDownIcon,
      color: 'danger',
      change: '-3.2%',
      description: 'Total de despesas do período'
    },
    {
      title: 'Lucro Líquido',
      value: `R$ ${indicadores.lucro.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: ArrowTrendingUpIcon,
      color: indicadores.lucro >= 0 ? 'success' : 'danger',
      change: indicadores.lucro >= 0 ? '+8.7%' : '-5.2%',
      description: 'Resultado líquido do período'
    },
    {
      title: 'Margem Bruta',
      value: `${indicadores.margemBruta}%`,
      icon: ChartBarIcon,
      color: 'primary',
      change: '+2.1%',
      description: 'Margem de lucratividade'
    },
    {
      title: 'Saldo Contas',
      value: `R$ ${indicadores.saldoContas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: BanknotesIcon,
      color: 'success',
      change: '+4.3%',
      description: 'Saldo total das contas'
    },
    {
      title: 'Lançamentos',
      value: indicadores.totalLancamentos,
      icon: CalendarIcon,
      color: 'primary',
      change: '+15%',
      description: 'Total de receitas e despesas do período'
    }
  ];

  const COLORS = ['#10B981', '#8B5CF6', '#3B82F6', '#EF4444', '#F59E0B'];

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Financeiro</h1>
            <p className="text-gray-600 mt-1">Visão geral do desempenho financeiro da empresa</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <ClockIcon className="h-4 w-4" />
            <span>Atualizado às {format(new Date(), 'HH:mm')}</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-8">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="form-group">
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Início
              </label>
              <input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <ChartBarIcon className="h-4 w-4 inline mr-1" />
                Regime
              </label>
              <select
                value={filtros.regime}
                onChange={(e) => setFiltros({ ...filtros, regime: e.target.value })}
                className="form-select"
              >
                <option value="caixa">Caixa</option>
                <option value="competencia">Competência</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <UserGroupIcon className="h-4 w-4 inline mr-1" />
                Centro de Custo
              </label>
              <select
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros({ ...filtros, centro_custo_id: e.target.value })}
                className="form-select"
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <BuildingLibraryIcon className="h-4 w-4 inline mr-1" />
                Conta Bancária
              </label>
              <select
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros({ ...filtros, conta_bancaria_id: e.target.value })}
                className="form-select"
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={carregarDados}
              className="btn-primary"
              disabled={carregando}
            >
              {carregando ? 'Atualizando...' : 'Aplicar Filtros'}
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {cards.map((card, index) => (
          <div key={index} className="card hover:shadow-md transition-shadow duration-200">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-2">{card.value}</p>
                  <div className="flex items-center mt-2">
                    <span className={`text-sm font-medium ${
                      card.change.startsWith('+') ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {card.change}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">vs período anterior</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{card.description}</p>
                </div>
                <div className={`p-3 rounded-full bg-${card.color}-100`}>
                  <card.icon className={`h-6 w-6 text-${card.color}-600`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Gráfico de Linha - Receitas vs Despesas */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Receitas vs Despesas (Mensal)</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={graficos.mensal}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="mes" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                    labelFormatter={(label) => `Mês: ${label}`}
                    contentStyle={{ 
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="receitas" 
                    name="Receitas"
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="despesas" 
                    name="Despesas"
                    stroke="#EF4444" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

      {/* Gráfico de Pizza - Categorias */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Distribuição por Categoria</h3>
          </div>
          <div className="card-body">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={graficos.categorias}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="valor"
                  >
                    {graficos.categorias.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Valor']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Últimos Lançamentos e Estatísticas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Últimos Lançamentos */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Últimos Lançamentos</h3>
          </div>
          <div className="card-body p-0">
            {ultimosLancamentos.length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th>Valor</th>
                      <th>Tipo</th>
                      <th>Conta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimosLancamentos.map((lancamento) => {
                      const tipoMeta = getTipoLancamentoMeta(lancamento.tipo);

                      return (
                      <tr key={lancamento.id}>
                        <td>{format(parseISO(lancamento.data), 'dd/MM/yyyy')}</td>
                        <td className="max-w-xs truncate">{lancamento.descricao}</td>
                        <td className={`font-medium ${tipoMeta.valueClass}`}>
                          {tipoMeta.signal} R$ {lancamento.valor.toFixed(2)}
                        </td>
                        <td>
                          <span className={tipoMeta.badgeClass}>
                            {tipoMeta.label}
                          </span>
                        </td>
                        <td>{lancamento.conta_bancaria_banco || 'Não informada'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <CurrencyDollarIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>Nenhum lançamento encontrado para o período selecionado</p>
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas Rápidas */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Estatísticas</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-success-100">
                  <CurrencyDollarIcon className="h-5 w-5 text-success-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Maior Receita</p>
                  <p className="text-lg font-semibold">
                    R$ {Math.max(...ultimosLancamentos.filter(l => l.tipo === 'receita').map(l => l.valor) || [0]).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-danger-100">
                  <ArrowTrendingDownIcon className="h-5 w-5 text-danger-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Maior Despesa</p>
                  <p className="text-lg font-semibold">
                    R$ {Math.max(...ultimosLancamentos.filter(l => l.tipo === 'despesa').map(l => l.valor) || [0]).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 rounded-lg bg-primary-100">
                  <ChartBarIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">Média Diária</p>
                  <p className="text-lg font-semibold">
                    R$ {((indicadores.faturamento + indicadores.despesas) / 30).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Resumo do Período</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Receitas:</span>
                  <span className="font-medium text-success-600">R$ {indicadores.faturamento.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Despesas:</span>
                  <span className="font-medium text-danger-600">R$ {indicadores.despesas.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-100">
                  <span>Resultado:</span>
                  <span className={indicadores.lucro >= 0 ? 'text-success-600' : 'text-danger-600'}>
                    R$ {indicadores.lucro.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dicas e Ações Rápidas */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <h4 className="font-semibold text-primary-800 mb-3">Dicas Financeiras</h4>
            <ul className="space-y-2 text-primary-700">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Revise despesas operacionais mensalmente</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Mantenha reserva para imprevistos</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Compare receitas vs despesas semanalmente</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="card bg-success-50 border-success-200">
          <div className="card-body">
            <h4 className="font-semibold text-success-800 mb-3">Próximas Ações</h4>
            <ul className="space-y-2 text-success-700">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Fechar lançamentos do mês</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Gerar DRE do trimestre</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Revisar saldos bancários</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="card bg-warning-50 border-warning-200">
          <div className="card-body">
            <h4 className="font-semibold text-warning-800 mb-3">Metas do Mês</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Meta de Faturamento</span>
                  <span>75%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-warning-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Controle de Despesas</span>
                  <span>90%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-success-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;



